// 클립보드 공유(설계서 §9.3.1) — 뷰어 ↔ 에이전트 "clip" DataChannel 프로토콜(순수 로직).
//
// 🔴 에이전트 `poc/rtc-core-rust/src/clip.rs` 와 **같은 규약**이다 — 상수·메시지 이름·오류 코드를 바꿀 때는 양쪽을 함께 바꾼다.
// 브라우저 전용 API 를 쓰지 않는다(TextEncoder·atob·btoa 는 Node 에도 있다) → Node 에서 그대로 시험한다.
//
// 메시지(JSON 텍스트, 필드 t):
//   에이전트→뷰어  hello {v, kinds, maxText, maxImage}          채널이 열리면 1회
//   양방향         put {id, kind, size, paste?} → data {id, seq, d}… → end {id}
//   뷰어→에이전트  get {id, copy?} · paste {id} · cancel {id}
//   에이전트→뷰어  ack {id, ok, err?}
//   (paste = 쓰지 않고 원격 OS 의 붙여넣기 단축키만 — hello `ops` 에 "paste" 가 있는 에이전트만, 2026-10-20)

/** DataChannel 이름(에이전트 `CLIP_CHANNEL_LABEL`). */
export const CLIP_CHANNEL_LABEL = 'clip';
/** 조각 하나의 원본 바이트 — base64 로 부풀어도 에이전트 수신 버퍼(64KB)에 들어간다. */
export const CHUNK_RAW_BYTES = 32 * 1024;
/** hello 가 상한을 주지 않았을 때의 기본값(에이전트 clip.rs 와 같은 값). */
export const DEFAULT_MAX_TEXT = 1024 * 1024;
export const DEFAULT_MAX_IMAGE = 10 * 1024 * 1024;
/** 뷰어가 받아들이는 이미지 픽셀 상한(압축 폭탄 방지 — 에이전트 MAX_IMAGE_PIXELS 와 같은 값). */
export const MAX_IMAGE_PIXELS = 40_000_000;
const MAX_ID_LEN = 64;

export const CLIP_MSG = {
  HELLO: 'hello',
  PUT: 'put',
  DATA: 'data',
  END: 'end',
  GET: 'get',
  PASTE: 'paste',
  CANCEL: 'cancel',
  ACK: 'ack',
} as const;

/** 오류 코드 — 앞쪽은 에이전트가 ack.err 로 보내는 값, 뒤쪽(TIMEOUT 이하)은 뷰어가 스스로 만드는 값. */
export const CLIP_ERR = {
  TOO_LARGE: 'too_large',
  CONTROL_OFF: 'control_off',
  UNSUPPORTED: 'unsupported',
  SECURE_DESKTOP: 'secure_desktop',
  EMPTY: 'empty',
  UNCHANGED: 'unchanged',
  BUSY: 'busy',
  PROTOCOL: 'protocol',
  FAILED: 'failed',
  TIMEOUT: 'timeout',
  CLOSED: 'closed',
  LOCAL_EMPTY: 'local_empty',
  LOCAL_READ: 'local_read',
} as const;

/** 오류 코드 → 사용자 문구. 무엇을 하면 되는지까지 말한다(원인만 말하면 같은 조작을 반복한다). */
export const CLIP_ERR_MESSAGE: Readonly<Record<string, string>> = {
  [CLIP_ERR.TOO_LARGE]: '크기가 너무 큽니다. 텍스트는 1MB, 이미지는 10MB까지 보낼 수 있습니다.',
  [CLIP_ERR.CONTROL_OFF]: '상대가 원격 입력 제어를 꺼 두어 클립보드를 주고받을 수 없습니다.',
  [CLIP_ERR.UNSUPPORTED]: '상대 기기가 이 형식을 받을 수 없습니다.',
  [CLIP_ERR.SECURE_DESKTOP]: '상대 화면이 잠금·로그인·관리자 확인(UAC) 화면이라 클립보드를 쓸 수 없습니다.',
  [CLIP_ERR.EMPTY]: '원격 클립보드가 비어 있습니다.',
  [CLIP_ERR.UNCHANGED]: '원격에서 복사된 내용이 없습니다. 원격 화면에서 먼저 선택하세요.',
  [CLIP_ERR.BUSY]: '원격 PC의 다른 프로그램이 클립보드를 쓰고 있습니다. 잠시 뒤 다시 시도하세요.',
  [CLIP_ERR.PROTOCOL]: '전송 중 오류가 났습니다. 다시 시도하세요.',
  [CLIP_ERR.FAILED]: '원격 클립보드 처리에 실패했습니다.',
  [CLIP_ERR.TIMEOUT]: '원격 기기가 응답하지 않습니다. 다시 시도하세요.',
  [CLIP_ERR.CLOSED]: '연결이 끊겨 클립보드를 주고받지 못했습니다.',
  [CLIP_ERR.LOCAL_EMPTY]: '내 클립보드에 보낼 내용(텍스트·이미지)이 없습니다.',
  [CLIP_ERR.LOCAL_READ]:
    '브라우저가 클립보드 읽기를 허용하지 않았습니다. 주소창의 권한 설정에서 클립보드를 허용하거나, 원격 화면에서 Ctrl+V 를 누르세요.',
};

export function clipErrorMessage(code: string): string {
  return CLIP_ERR_MESSAGE[code] ?? `클립보드 공유에 실패했습니다(${code}).`;
}

/** 클립보드 공유 실패 — `code` 로 분기하고 `message` 를 그대로 보여 준다. */
export class ClipError extends Error {
  readonly code: string;
  constructor(code: string, message?: string) {
    super(message ?? clipErrorMessage(code));
    this.name = 'ClipError';
    this.code = code;
  }
}

export type ClipKind = 'text' | 'image';

/** 클립보드 내용 — 텍스트 또는 **PNG** 이미지(에이전트와 PNG 로만 주고받는다). */
export type ClipContent = { kind: 'text'; text: string } | { kind: 'image'; png: Uint8Array };

/** 에이전트가 hello 로 알린 능력. */
export interface ClipCaps {
  version: number;
  kinds: ClipKind[];
  maxText: number;
  maxImage: number;
  /** 에이전트가 `paste`(원격 클립보드 그대로 붙여넣기)를 안다 — hello `ops`. 구 에이전트는 false. */
  remotePaste: boolean;
}

function isKind(v: unknown): v is ClipKind {
  return v === 'text' || v === 'image';
}

/** hello 메시지 → 능력. 형식이 틀리면 null(클립보드 기능을 켜지 않는다). */
export function parseHello(v: Record<string, unknown>): ClipCaps | null {
  if (v.t !== CLIP_MSG.HELLO || !Array.isArray(v.kinds)) {
    return null;
  }
  const kinds = v.kinds.filter(isKind);
  if (kinds.length === 0) {
    return null;
  }
  const num = (x: unknown, d: number) => (typeof x === 'number' && x > 0 ? x : d);
  return {
    version: num(v.v, 1),
    kinds,
    maxText: num(v.maxText, DEFAULT_MAX_TEXT),
    maxImage: num(v.maxImage, DEFAULT_MAX_IMAGE),
    remotePaste: Array.isArray(v.ops) && v.ops.includes(CLIP_MSG.PASTE),
  };
}

export function maxBytes(caps: Pick<ClipCaps, 'maxText' | 'maxImage'>, kind: ClipKind): number {
  return kind === 'text' ? caps.maxText : caps.maxImage;
}

const utf8 = new TextEncoder();

/** 전송되는 원본 바이트(텍스트=UTF-8, 이미지=PNG). */
export function contentBytes(c: ClipContent): Uint8Array {
  return c.kind === 'text' ? utf8.encode(c.text) : c.png;
}

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

export function isPng(b: Uint8Array): boolean {
  return b.length >= PNG_SIGNATURE.length && PNG_SIGNATURE.every((x, i) => b[i] === x);
}

/** 바이트 → base64. 큰 배열도 스택을 넘기지 않게 나눠 문자열로 만든다. */
export function toBase64(bytes: Uint8Array): string {
  let bin = '';
  const step = 0x8000;
  for (let i = 0; i < bytes.length; i += step) {
    bin += String.fromCharCode(...bytes.subarray(i, i + step));
  }
  return btoa(bin);
}

/** base64 → 바이트. 형식이 틀리면 ClipError(protocol). */
export function fromBase64(s: string): Uint8Array {
  let bin: string;
  try {
    bin = atob(s);
  } catch {
    throw new ClipError(CLIP_ERR.PROTOCOL, 'base64 형식 오류');
  }
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    out[i] = bin.charCodeAt(i);
  }
  return out;
}

/** 보내기 전에 상대 능력·상한을 확인한다(보낸 뒤 거절당하는 것보다 빨리, 정확한 이유로 알린다). */
export function checkSendable(c: ClipContent, caps: ClipCaps): void {
  if (!caps.kinds.includes(c.kind)) {
    throw new ClipError(
      CLIP_ERR.UNSUPPORTED,
      c.kind === 'image' ? '상대 기기는 이미지를 받을 수 없습니다(텍스트만 지원).' : undefined,
    );
  }
  if (contentBytes(c).length > maxBytes(caps, c.kind)) {
    throw new ClipError(CLIP_ERR.TOO_LARGE);
  }
}

/** 내용 하나를 put + data… + end 메시지 열로 쪼갠다. `paste` = 에이전트가 쓰고 나서 붙여넣기 단축키를 누른다. */
export function encodeFrames(id: string, c: ClipContent, paste: boolean): string[] {
  const bytes = contentBytes(c);
  const frames = [JSON.stringify({ t: CLIP_MSG.PUT, id, kind: c.kind, size: bytes.length, paste })];
  for (let seq = 0, at = 0; at < bytes.length; seq++, at += CHUNK_RAW_BYTES) {
    frames.push(
      JSON.stringify({ t: CLIP_MSG.DATA, id, seq, d: toBase64(bytes.subarray(at, at + CHUNK_RAW_BYTES)) }),
    );
  }
  frames.push(JSON.stringify({ t: CLIP_MSG.END, id }));
  return frames;
}

const utf8Strict = new TextDecoder('utf-8', { fatal: true });

/** 받는 쪽 조립기(에이전트→뷰어 put/data/end). 조각 순서·누적 크기를 검사한다. */
export class Incoming {
  readonly id: string;
  private readonly kind: ClipKind;
  private readonly size: number;
  private readonly buf: Uint8Array;
  private filled = 0;
  private nextSeq = 0;

  private constructor(id: string, kind: ClipKind, size: number) {
    this.id = id;
    this.kind = kind;
    this.size = size;
    this.buf = new Uint8Array(size);
  }

  /** put 메시지로 시작. 선언 크기가 상한을 넘으면 받기 전에 거절한다. */
  static begin(v: Record<string, unknown>, limits: Pick<ClipCaps, 'maxText' | 'maxImage'>): Incoming {
    const id = typeof v.id === 'string' ? v.id : '';
    if (!id || id.length > MAX_ID_LEN) {
      throw new ClipError(CLIP_ERR.PROTOCOL, '전송 id 없음');
    }
    if (!isKind(v.kind)) {
      throw new ClipError(CLIP_ERR.UNSUPPORTED);
    }
    const size = v.size;
    if (typeof size !== 'number' || !Number.isInteger(size) || size < 0) {
      throw new ClipError(CLIP_ERR.PROTOCOL, 'size 형식 오류');
    }
    if (size > maxBytes(limits, v.kind)) {
      throw new ClipError(CLIP_ERR.TOO_LARGE);
    }
    return new Incoming(id, v.kind, size);
  }

  push(seq: unknown, d: unknown): void {
    if (seq !== this.nextSeq || typeof d !== 'string') {
      throw new ClipError(CLIP_ERR.PROTOCOL, `조각 순서 어긋남(기대 ${this.nextSeq})`);
    }
    const bytes = fromBase64(d);
    if (this.filled + bytes.length > this.size) {
      throw new ClipError(CLIP_ERR.PROTOCOL, '선언 크기 초과');
    }
    this.buf.set(bytes, this.filled);
    this.filled += bytes.length;
    this.nextSeq += 1;
  }

  finish(): ClipContent {
    if (this.filled !== this.size) {
      throw new ClipError(CLIP_ERR.PROTOCOL, `크기 불일치(선언 ${this.size} 받음 ${this.filled})`);
    }
    if (this.kind === 'text') {
      try {
        return { kind: 'text', text: utf8Strict.decode(this.buf) };
      } catch {
        throw new ClipError(CLIP_ERR.PROTOCOL, 'UTF-8 아님');
      }
    }
    if (!isPng(this.buf)) {
      throw new ClipError(CLIP_ERR.UNSUPPORTED, 'PNG 아님');
    }
    return { kind: 'image', png: this.buf };
  }
}

/** 사람이 읽는 요약("텍스트 12자" · "이미지 34KB"). 🔴 내용은 담지 않는다(비밀번호일 수 있다). */
export function describeContent(c: ClipContent): string {
  if (c.kind === 'text') {
    return `텍스트 ${[...c.text].length.toLocaleString()}자`;
  }
  const kb = c.png.length / 1024;
  return kb >= 1024 ? `이미지 ${(kb / 1024).toFixed(1)}MB` : `이미지 ${Math.max(1, Math.round(kb))}KB`;
}

let idSeq = 0;

/** 전송 id(세션 안에서만 구분되면 된다). */
export function newClipId(): string {
  idSeq += 1;
  return `v${Date.now().toString(36)}${idSeq}`;
}
